import React, { useEffect, useState } from "react";
import { getProfile } from "api/user/profile";
import { getAllCodeList, getCodeList, getInnovationCodeList } from "api/user/code";
import { checkToken } from "api/user/user";
import { useTranslation } from "react-i18next";
import useRoute from "hooks/useRoute";

const UserContext = React.createContext();

const { Provider } = UserContext;

const UserProvider = ({ children }) => {
    const { t } = useTranslation();
    // 마이페이지 관리자 메뉴 사용 여부
    const [menuAuth, setMenuAuth] = useState(false);
    const [userInfo, setUserInfo] = useState({
        dataState: false, // 컨텍스트 데이터 유무
        loggedIn: false, // 로그인 여부
        keepLogin: false, // 로그인 유지
        user: {}, // 사용자 정보
        illuCode: {}, // 일루넥스 코드
        innoCode: {}, // 이노베이션 코드
    });
    const navigate = useRoute();

    // 프로필 세션값
    const userValues = {
        jwt: "jwt",
        name: "name",
        email: "email",
        comName: "comName",
        position: "position",
        imageUrl: "imageUrl",
        null: "null",
        levelCd: "levelCd",
        illuCode: "illuCode",
        innoCode: "innoCode",
    };
    const localStorageValue = localStorage.getItem(userValues.jwt);

    function getToken() {
        return localStorageValue ? localStorageValue : sessionStorage.getItem(userValues.jwt);
    }

    const token = getToken();
    const [codeList, setCodeList] = useState({});
    const [codeState, setCodeState] = useState(false);

    const checkTokenExpired = (res) => {
        if (res.data.status === 102) {
            logout();
        }
        return res;
    };

    // 일루넥스 코드를 코드명으로 반환
    const getIlluCodeToValue = (code) => {
        return userInfo.illuCode[code];
    };

    // 일루넥스코드 앞에 5자리를 보내면 그에 해당하는 그룹에 포함된 데이터를 배열로 반환
    const getIlluCodeGroup = (firstSecond) => {
        let group = [];
        Object.keys(userInfo.illuCode).map((value) => {
            if (value.startsWith(firstSecond) && !value.endsWith("00")) {
                // let a = value.toString();
                group.push({ key: value, value: userInfo.illuCode[value] });
            }
        });
        return group;
    };

    // 이노베이션 코드를 코드명으로 반환
    const getInnoCodeToValue = (code) => {
        return userInfo.innoCode[code];
    };

    const getCode = (codes) => {
        getCodeList(token, codes)
            .then((res) => {
                codes.split(",").forEach((code) => {
                    let list = [];
                    Object.entries(res.data.data[code.trim()]).forEach((o) => {
                        list.push({
                            key: o[0],
                            value: o[1],
                        });
                    });
                    res.data.data[code.trim()] = list;
                });
                setCodeList(res.data.data);
                setCodeState(true);
            })
            .catch((err) => {
                setCodeState(false);
            });
    };

    const getUserInfo = async (data, keepLoginState) => {
        if (keepLoginState) {
            setUserInfo({
                dataState: true,
                loggedIn: true,
                keepLogin: true,
                user: data,
            });
            localStorage.setItem(userValues.jwt, data.token);
        } else {
            // 로그인유지
            setUserInfo({
                dataState: true,
                loggedIn: true,
                keepLogin: false,
                user: data,
            });
            sessionStorage.setItem(userValues.jwt, data.token);
        }
    };

    const checkMenuAuth = () => {
        if (userInfo.user.comName === null || userInfo.user.levelCd === "ALT0003") {
            setMenuAuth(false);
        } else {
            setMenuAuth(true);
        }
    };

    const logout = (isOpenInnovation) => {
        // 오픈이노베이션에서 로그아웃 시 대시보드로 이동
        if (isOpenInnovation) navigate("/openInnovation/dashboard");
        else navigate("/");

        sessionStorage.clear();
        localStorage.clear();
        setUserInfo({
            dataState: false,
            loggedIn: false,
            keepLogin: false,
            user: {},
            illuCode: {},
            innoCode: {},
        });
    };

    const getProfileInfo = async () => {
        // 토큰 유효 체크
        if (token !== null) {
            await checkToken(token).catch((err) => {
                if (err.response !== undefined && err.response.status === 401) {
                    alert(t("common.exception.expire"));
                    logout();
                }
            });
        }

        // 여러 탭으로 열 때 업데이트된 profile로 보이기 위해 getProfile token이 있을때마다 호출
        let name;
        let email;
        let comName;
        let position;
        let imageUrl;
        let levelCd;
        let illuCode = sessionStorage.getItem(userValues.illuCode) ? JSON.parse(sessionStorage.getItem(userValues.illuCode)) : null;
        let innoCode = sessionStorage.getItem(userValues.innoCode) ? JSON.parse(sessionStorage.getItem(userValues.innoCode)) : null;

        if (!illuCode || !innoCode) {
            // 일루넥스 코드 불러와서 세션에 담기
            let illuCodeRes = await getAllCodeList(token);
            if (illuCodeRes.data.status === 0) {
                illuCode = illuCodeRes.data.data;
                sessionStorage.setItem(userValues.illuCode, JSON.stringify(illuCodeRes.data.data));
            }
            // 이노베이션 코드 불러와서 세션에 담기
            let innoCodeRes = await getInnovationCodeList(token);
            if (innoCodeRes.data.status === 0) {
                innoCode = innoCodeRes.data.data;
                sessionStorage.setItem(userValues.innoCode, JSON.stringify(innoCodeRes.data.data));
            }
        }

        let res = await getProfile(token);
        if (res.data.status === 0) {
            name = res.data.data.name;
            email = res.data.data.email;
            comName = res.data.data.accountCompanies[0].company ? res.data.data.accountCompanies[0].company.comName : null;
            position = res.data.data.position;
            imageUrl = res.data.data.imageUrl;
            levelCd = res.data.data.accountCompanies[0].levelCd;
        } else {
            logout();
        }

        let data = {
            name: name,
            comName: comName,
            position: position !== userValues.null ? position : null,
            imageUrl: imageUrl !== userValues.null ? imageUrl : null,
            levelCd: levelCd,
        };

        if (localStorageValue) {
            setUserInfo({
                dataState: true,
                loggedIn: true,
                keepLogin: true,
                user: data,
                illuCode: illuCode,
                innoCode: innoCode,
            });
        } else {
            setUserInfo({
                dataState: true,
                loggedIn: true,
                keepLogin: false,
                user: data,
                illuCode: illuCode,
                innoCode: innoCode,
            });
        }
        setTimeout(() => {
            logout();
        }, 21600000); // 6시간 후 자동 로그아웃
    };

    const changeProfileInfo = (...value) => {
        value.map((v) => {
            return sessionStorage.removeItem(v);
        });
    };

    useEffect(() => {
        if (!token) return;
        getProfileInfo();
    }, [token]);

    return (
        <Provider
            value={{
                checkTokenExpired,
                token,
                getCode,
                getIlluCodeToValue,
                getIlluCodeGroup,
                getInnoCodeToValue,
                getUserInfo,
                getProfileInfo,
                changeProfileInfo,
                checkMenuAuth,
                logout,
                codeList,
                codeState,
                userInfo,
                userValues,
                menuAuth,
            }}
        >
            {children}
        </Provider>
    );
};

export { UserContext, UserProvider };
